import { styleCycleFills } from './cycleFills';
import type { DrawingDocument } from '../model';
import type { EditorSelection } from './editor.ts';

/**
 * #868 (#842 S7; owner 2026-10-07: a segment can be styled too) -- molecule appearance, stored per
 * atom and per bond in the depiction, so a whole molecule or any part of one can be styled and a
 * join or split keeps each part's look. Atoms take a label colour and "colour by element"; bonds a
 * colour and a width in points. Never dashes: they would read as stereo.
 */
export type AtomAppearance = NonNullable<DrawingDocument['depiction']['atomAppearance']>[string];
export type BondAppearance = NonNullable<DrawingDocument['depiction']['bondAppearance']>[string];

/** A change to apply; a property set to `undefined` goes back to the standard look. */
export interface MoleculeAppearancePatch {
  readonly cycleFill?: string | undefined;
  readonly color?: string | undefined;
  readonly lineWidth?: number | undefined;
  readonly colorByElement?: boolean | undefined;
}

// Journal-style heteroatom colours, chosen to read on white paper (and on the export's white).
// C and H keep the atom's own colour.
export const ELEMENT_COLORS: Readonly<Record<number, string>> = {
  7: '#2f5fd0', // N
  8: '#d0312d', // O
  9: '#1e8c3a', // F
  15: '#e07b00', // P
  16: '#b8860b', // S
  17: '#1e8c3a', // Cl
  35: '#a0522d', // Br
  53: '#7b4fbf' // I
};

/** The atoms and bonds a selection styles: its atoms, its bonds, and every bond between two of its atoms. */
export function appearanceTargets(
  document: DrawingDocument,
  selection: EditorSelection
): { readonly atomIds: readonly string[]; readonly bondIds: readonly string[] } {
  const atomIds = selection.atomIds.filter((id) => document.structure.atoms[id]);
  const selectedAtoms = new Set(atomIds);
  const bondIds = new Set(selection.bondIds.filter((id) => document.structure.bonds[id]));
  for (const edge of document.structure.graph.edges) {
    if (selectedAtoms.has(edge.source) && selectedAtoms.has(edge.target)) bondIds.add(edge.id);
  }
  return { atomIds, bondIds: [...bondIds] };
}

function patched<T extends object>(current: T | undefined, patch: Partial<Record<keyof T, unknown>>): T | undefined {
  const next: Record<string, unknown> = { ...current };
  for (const [key, value] of Object.entries(patch)) {
    if (value === undefined) delete next[key];
    else next[key] = value;
  }
  return Object.keys(next).length > 0 ? (next as T) : undefined;
}

function updateMap<T extends object>(
  map: Readonly<Record<string, T>> | undefined,
  ids: readonly string[],
  patch: Partial<Record<keyof T, unknown>>
): Record<string, T> | undefined {
  if (ids.length === 0 || Object.keys(patch).length === 0) return map;
  const next: Record<string, T> = { ...map };
  for (const id of ids) {
    const value = patched(next[id], patch);
    if (value) next[id] = value;
    else delete next[id];
  }
  return Object.keys(next).length > 0 ? next : undefined;
}

/** Applies `patch` to the selection's atoms (colour, by element) and bonds (colour, width). */
export function styleMolecules(
  document: DrawingDocument,
  selection: EditorSelection,
  patch: MoleculeAppearancePatch
): DrawingDocument {
  if ('cycleFill' in patch) document = styleCycleFills(document, selection, patch.cycleFill);
  const { atomIds, bondIds } = appearanceTargets(document, selection);
  const atomPatch = Object.fromEntries(
    Object.entries(patch).filter(([key]) => key === 'color' || key === 'colorByElement')
  );
  const bondPatch = Object.fromEntries(Object.entries(patch).filter(([key]) => key === 'color' || key === 'lineWidth'));
  const atomAppearance = updateMap(document.depiction.atomAppearance, atomIds, atomPatch);
  const bondAppearance = updateMap(document.depiction.bondAppearance, bondIds, bondPatch);
  if (
    JSON.stringify(atomAppearance ?? {}) === JSON.stringify(document.depiction.atomAppearance ?? {}) &&
    JSON.stringify(bondAppearance ?? {}) === JSON.stringify(document.depiction.bondAppearance ?? {})
  ) {
    return document;
  }
  const { atomAppearance: _atoms, bondAppearance: _bonds, ...depiction } = document.depiction;
  return {
    ...document,
    depiction: {
      ...depiction,
      ...(atomAppearance ? { atomAppearance } : {}),
      ...(bondAppearance ? { bondAppearance } : {})
    }
  };
}

/** The colour an atom's label is drawn in, given the colour the drawing would otherwise use. */
export function atomLabelColor(
  appearance: AtomAppearance | undefined,
  atomicNumber: number,
  fallback: string | undefined
): string | undefined {
  if (appearance?.colorByElement && ELEMENT_COLORS[atomicNumber]) return ELEMENT_COLORS[atomicNumber];
  return appearance?.color ?? fallback;
}
